import React, { useState } from 'react';
import Collapsible from 'react-collapsible';
import { TourPackage } from '../models/tour-package.model';

interface TourPackageDetailsProps {
    packageItem: TourPackage;
    idx: number;
    startDate: Date;
    travellers: number;
}

function TourPackageDetails({ packageItem, idx, startDate, travellers }: TourPackageDetailsProps) {
    const [isItineraryOpen, setIsItineraryOpen] = useState<boolean>(false);

    const formattedDate = startDate.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });

    const getWhatsAppLink = () => {
        const message = `Hi Aapli Gaadi, I want to book the Tour Package: "${packageItem.name}" (${packageItem.duration}).\n\nStart Date: ${formattedDate}\nNumber of Travellers: ${travellers}\nApprox. Price: ₹${packageItem.price * travellers} (₹${packageItem.price}/person)\n\nPlease share booking confirmation details!`;
        return `https://wa.me/917208388380?text=${encodeURIComponent(message)}`;
    };

    return (
        <div className="car-wrap rounded mb-5 p-4 bg-white shadow-sm" style={{ border: '1px solid #e6e6e6' }}>
            <div className="row">
                {/* Image Section */}
                <div className="col-lg-5 col-md-12 mb-4 mb-lg-0">
                    <div 
                        className="img rounded d-flex align-items-end" 
                        style={{ 
                            backgroundImage: `url('${process.env.PUBLIC_URL}/image/blogs/${packageItem.image}')`, 
                            backgroundSize: 'cover', 
                            backgroundPosition: 'center',
                            height: '300px',
                            width: '100%',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div className="p-3 w-100" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0))', borderRadius: '0 0 .25rem .25rem' }}>
                            <span className="badge badge-primary py-2 px-3" style={{ fontSize: '14px', borderRadius: '20px' }}>{packageItem.duration}</span>
                        </div>
                    </div>
                </div>

                {/* Main Package Details Section */}
                <div className="col-lg-7 col-md-12 d-flex flex-column justify-content-between">
                    <div>
                        <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                            <h2 className="mb-0 text-dark font-weight-bold" style={{ fontSize: '24px', lineHeight: '1.3' }}>
                                {packageItem.name}
                            </h2>
                            <div className="text-right mt-2 mt-md-0">
                                <span className="text-primary font-weight-bold" style={{ fontSize: '26px' }}>₹{packageItem.price}</span>
                                <span className="text-muted" style={{ fontSize: '14px' }}> / Traveller</span>
                            </div>
                        </div>

                        <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: '1.6' }}>{packageItem.description}</p>

                        <div className="row mb-4">
                            {/* Highlights */}
                            <div className="col-md-6 mb-3 mb-md-0">
                                <h4 className="h6 font-weight-bold text-uppercase text-primary mb-2" style={{ letterSpacing: '1px' }}>Highlights</h4>
                                <ul className="list-unstyled" style={{ fontSize: '14px' }}>
                                    {packageItem.highlights.map((highlight, index) => (
                                        <li key={index} className="mb-1 text-dark d-flex align-items-start">
                                            <span className="text-success mr-2">✓</span>
                                            <span>{highlight}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Inclusions */}
                            <div className="col-md-6">
                                <h4 className="h6 font-weight-bold text-uppercase text-primary mb-2" style={{ letterSpacing: '1px' }}>Inclusions</h4>
                                <ul className="list-unstyled" style={{ fontSize: '14px' }}>
                                    {packageItem.inclusions.map((inclusion, index) => (
                                        <li key={index} className="mb-1 text-dark d-flex align-items-start">
                                            <span className="text-info mr-2">✦</span>
                                            <span>{inclusion}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Booking and Details Toggle Buttons */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap pt-3 border-top mt-auto">
                        <button 
                            className="btn btn-outline-primary py-2 px-3 mb-2 mb-sm-0" 
                            style={{ borderRadius: '30px', fontSize: '14px', fontWeight: 600 }}
                            onClick={() => setIsItineraryOpen(!isItineraryOpen)}
                        >
                            {isItineraryOpen ? 'Hide Daily Itinerary ▲' : 'View Daily Itinerary ▼'}
                        </button>

                        <div className="d-flex align-items-center flex-wrap">
                            {travellers > 1 && (
                                <span className="mr-3 text-dark font-weight-bold" style={{ fontSize: '15px' }}>
                                    Total: ₹{packageItem.price * travellers} <small className="text-muted">({travellers} Travellers)</small>
                                </span>
                            )}
                            <a 
                                href={getWhatsAppLink()} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="btn btn-primary py-2 px-4" 
                                style={{ borderRadius: '30px', fontSize: '14px', fontWeight: 600 }}
                            >
                                <i className="fab fa-whatsapp mr-2"></i> Book Package Now
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Itinerary Accordion Section */}
            {isItineraryOpen && (
                <div className="mt-4 pt-4 border-top" style={{ backgroundColor: '#fafafa', borderRadius: '8px', padding: '15px' }}>
                    <h3 className="h5 text-dark font-weight-bold mb-4">Detailed Day-by-Day Itinerary</h3>
                    <div className="itinerary-timeline pl-3" style={{ borderLeft: '2px solid #ffbc00', position: 'relative' }}>
                        {packageItem.itinerary.map((dayItem, index) => (
                            <div key={index} className="itinerary-day-block mb-4" style={{ position: 'relative' }}>
                                <div 
                                    className="day-indicator d-flex align-items-center justify-content-center bg-primary text-white font-weight-bold" 
                                    style={{ 
                                        position: 'absolute', 
                                        left: '-28px', 
                                        top: '0px', 
                                        width: '24px', 
                                        height: '24px', 
                                        borderRadius: '50%',
                                        fontSize: '12px' 
                                    }}
                                >
                                    {dayItem.day}
                                </div>
                                <div className="ml-3">
                                    <h4 className="h6 text-dark font-weight-bold mb-2" style={{ fontSize: '16px' }}>
                                        Day {dayItem.day}: {dayItem.title}
                                    </h4>
                                    <ul className="pl-3" style={{ fontSize: '14px', color: '#555', lineHeight: '1.6' }}>
                                        {dayItem.activities.map((activity, actIdx) => (
                                            <li key={actIdx} className="mb-2">{activity}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default TourPackageDetails;
